import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCollection, useDoc } from '../data/firestore';
import { useSuperadmin } from '../data/permissions';
import { brainPlatform } from '../data/legalApi';
import Tabs from '../components/Tabs';
import Markdown, { stripFrontmatter } from '../components/Markdown';
import './Brain.css';

// The Brain — the operator's view of everything this app knows about itself.
//
// Reads the compiled repo brain (brainPages / brainMeta, written by
// scripts/sync-brain.mjs) straight from Firestore under the superadmin rule,
// and the platform roll-up (every org's project brains + the knowledge
// corpus) from /admin/brain/platform. The point of the page is coverage:
// what is catalogued, how fresh and confident it is, and where the brain is
// thin — broken links, orphans, missing frontmatter, stale pages, source
// files no page names.

const KIND_LABEL = { system: 'Systems', decision: 'Decisions', lesson: 'Lessons', runbook: 'Runbooks', concept: 'Concepts', skill: 'Skills', index: 'Index', log: 'Log', schema: 'Schema', other: 'Other' };
const KIND_DESC = {
  system: 'How a subsystem works and why', decision: 'Choices made, with the alternatives', lesson: 'Learned the hard way',
  runbook: 'Step-by-step procedures', concept: 'Ideas and vocabulary', skill: 'Reusable agent workflows',
};
const ISSUE_LABEL = {
  'broken-link': 'Broken wikilinks', 'no-frontmatter': 'No frontmatter', 'missing-field': 'Missing frontmatter fields',
  'low-confidence': 'Low confidence', 'bad-status': 'Invalid status', stale: 'Stale (not updated recently)',
  'source-missing': 'Source file no longer exists', orphan: 'Orphans (no inbound links)', thin: 'Thin pages (< 80 words)',
};
const ISSUE_HINT = {
  'broken-link': 'Fix the link target or create the page.', 'no-frontmatter': 'Add the schema frontmatter block.',
  'missing-field': 'Fill the field; brain-lint fails CI without it.', 'low-confidence': 'Verify against the code and raise confidence, or note what is uncertain.',
  'bad-status': 'Use active or superseded.', stale: 'Re-read against the current code and bump `updated`, or mark superseded.',
  'source-missing': 'Point sources at the file that replaced it.', orphan: 'Link to it from the system or decision it belongs to.',
  thin: 'Either flesh it out or fold it into a fuller page.',
};

const fmtDate = (ms) => (ms ? new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const fmtDay = (s) => s || '—';
const ago = (ms) => {
  if (!ms) return '—';
  const d = Math.floor((Date.now() - ms) / 86400000);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d} days ago`;
};
const statusBadge = (s) => (s === 'active' ? 'b-ok' : s === 'superseded' ? 'b-muted' : 'b-warn');
const confBadge = (c) => (c === 'high' ? 'b-ok' : c === 'medium' ? 'b-info' : c === 'low' ? 'b-warn' : 'b-muted');
const kindBadge = (k) => ({ system: 'b-info', decision: 'b-ok', lesson: 'b-warn', runbook: 'b-muted', concept: 'b-muted' }[k] || 'b-muted');

function Bar({ parts }) {
  const total = parts.reduce((a, p) => a + p.n, 0) || 1;
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
      <div className="brain-bar" role="img" aria-label={parts.map((p) => `${p.label} ${p.n}`).join(', ')}>
        {parts.filter((p) => p.n > 0).map((p) => <span key={p.label} style={{ width: `${(p.n / total) * 100}%`, background: p.color }} />)}
      </div>
      <div className="brain-legend">{parts.map((p) => <span key={p.label}><i style={{ background: p.color }} />{p.label} <strong>{p.n}</strong></span>)}</div>
    </div>
  );
}

// ---- Overview --------------------------------------------------------------
function Overview({ summary, pages, go }) {
  const wiki = pages.filter((p) => p.path?.startsWith('brain/wiki/'));
  const fresh = useMemo(() => {
    const b = { '7 days': 0, '30 days': 0, '60 days': 0, older: 0, undated: 0 };
    for (const p of wiki) {
      const u = p.meta?.updated ? Date.parse(p.meta.updated) : null;
      if (!u) { b.undated += 1; continue; }
      const d = (Date.now() - u) / 86400000;
      if (d <= 7) b['7 days'] += 1; else if (d <= 30) b['30 days'] += 1; else if (d <= 60) b['60 days'] += 1; else b.older += 1;
    }
    return b;
  }, [wiki]);
  const t = summary.totals || {};
  const cov = summary.coverage || {};
  const lint = summary.lint || {};
  const tags = Object.entries(summary.tags || {}).sort((a, b) => b[1] - a[1]).slice(0, 24);
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-6)' }}>
      <div className="brain-strip">
        <div className="cs-stat is-compact"><span className="cs-stat-label">Wiki pages</span><span className="cs-stat-value">{t.wiki ?? 0}</span></div>
        <div className="cs-stat is-compact"><span className="cs-stat-label">Words</span><span className="cs-stat-value">{(t.words || 0).toLocaleString()}</span></div>
        <div className="cs-stat is-compact"><span className="cs-stat-label">Lint</span><span className="cs-stat-value" style={{ color: lint.ok ? 'var(--ok)' : 'var(--danger)' }}>{lint.ok ? 'clean' : `${(lint.errors || []).length} err`}</span></div>
        <div className="cs-stat is-compact"><span className="cs-stat-label">Pages with issues</span><span className="cs-stat-value" style={{ color: summary.pagesWithIssues ? 'var(--warn)' : 'var(--ok)' }}>{summary.pagesWithIssues || 0}</span></div>
        <div className="cs-stat is-compact"><span className="cs-stat-label">Code catalogued</span><span className="cs-stat-value">{cov.total ? `${Math.round((cov.documented / cov.total) * 100)}%` : '—'}</span><span className="cs-stat-delta u-muted">{cov.documented}/{cov.total} files</span></div>
        <div className="cs-stat is-compact"><span className="cs-stat-label">Log entries</span><span className="cs-stat-value">{t.logEntries ?? 0}</span>{summary.logMalformed ? <span className="cs-stat-delta" style={{ color: 'var(--warn)' }}>{summary.logMalformed} malformed</span> : null}</div>
      </div>

      <section>
        <h3 style={{ margin: '0 0 var(--sp-3)' }}>By kind</h3>
        <div className="brain-kinds">
          {['system', 'decision', 'lesson', 'runbook', 'concept', 'skill'].map((k) => (
            <button type="button" key={k} className="brain-kind" onClick={() => go('catalogue', { kind: k })}>
              <span className="brain-kind-n">{k === 'skill' ? (t.skills || 0) : (summary.byKind?.[k] || 0)}</span>
              <span className="brain-kind-l">{KIND_LABEL[k]}</span>
              <span className="brain-kind-d">{KIND_DESC[k]}</span>
            </button>
          ))}
        </div>
      </section>

      <div className="u-grid" style={{ '--min': '280px', '--gap': 'var(--sp-4)' }}>
        <div className="cs-card" style={{ padding: 'var(--sp-4)' }}>
          <h4 style={{ margin: '0 0 var(--sp-3)' }}>Status</h4>
          <Bar parts={[{ label: 'active', n: summary.byStatus?.active || 0, color: 'var(--ok)' }, { label: 'superseded', n: summary.byStatus?.superseded || 0, color: 'var(--muted)' }, { label: 'other', n: (summary.byStatus?.unknown || 0), color: 'var(--warn)' }]} />
        </div>
        <div className="cs-card" style={{ padding: 'var(--sp-4)' }}>
          <h4 style={{ margin: '0 0 var(--sp-3)' }}>Confidence</h4>
          <Bar parts={[{ label: 'high', n: summary.byConfidence?.high || 0, color: 'var(--ok)' }, { label: 'medium', n: summary.byConfidence?.medium || 0, color: 'var(--info)' }, { label: 'low', n: summary.byConfidence?.low || 0, color: 'var(--warn)' }, { label: 'unset', n: summary.byConfidence?.unknown || 0, color: 'var(--danger)' }]} />
        </div>
        <div className="cs-card" style={{ padding: 'var(--sp-4)' }}>
          <h4 style={{ margin: '0 0 var(--sp-3)' }}>Freshness (frontmatter `updated`)</h4>
          <Bar parts={[{ label: '≤ 7 days', n: fresh['7 days'], color: 'var(--ok)' }, { label: '≤ 30', n: fresh['30 days'], color: 'var(--info)' }, { label: '≤ 60', n: fresh['60 days'], color: 'var(--warn)' }, { label: 'older', n: fresh.older, color: 'var(--danger)' }, { label: 'undated', n: fresh.undated, color: 'var(--muted)' }]} />
        </div>
      </div>

      <section>
        <h3 style={{ margin: '0 0 var(--sp-3)' }}>Tags</h3>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
          {tags.map(([tag, n]) => <button type="button" key={tag} className="cs-chip" onClick={() => go('catalogue', { tag })}>{tag} <span className="u-muted">{n}</span></button>)}
        </div>
      </section>

      <section>
        <h3 style={{ margin: '0 0 var(--sp-3)' }}>Recent log</h3>
        <div className="cs-card" style={{ padding: '0 var(--sp-4)' }}>
          {(summary.log || []).slice(0, 8).map((e, i) => (
            <div key={i} className="brain-log-item">
              <span className="brain-log-date">{fmtDay(e.date)}</span>
              <div>
                <div><strong>{e.title}</strong> {e.type && <span className="cs-badge b-muted" style={{ marginLeft: 'var(--sp-2)' }}>{e.type}</span>}</div>
                <div className="u-small u-muted u-clamp" style={{ '--lines': 2 }}>{e.excerpt}</div>
              </div>
            </div>
          ))}
        </div>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" style={{ marginTop: 'var(--sp-2)' }} onClick={() => go('log')}>Full log →</button>
      </section>
    </div>
  );
}

// ---- Catalogue -------------------------------------------------------------
function Catalogue({ pages, params, setParams }) {
  const navigate = useNavigate();
  const kind = params.get('kind') || '';
  const tag = params.get('tag') || '';
  const status = params.get('status') || '';
  const conf = params.get('conf') || '';
  const qText = params.get('q') || '';
  const only = params.get('issues') === '1';
  const set = (k, v) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); setParams(n, { replace: true }); };

  const list = useMemo(() => {
    const ql = qText.trim().toLowerCase();
    return pages
      .filter((p) => p.kind !== 'log' && p.kind !== 'index' && p.kind !== 'schema')
      .filter((p) => !kind || p.kind === kind)
      .filter((p) => !tag || (p.meta?.tags || []).includes(tag))
      .filter((p) => !status || p.meta?.status === status)
      .filter((p) => !conf || p.meta?.confidence === conf)
      .filter((p) => !only || (p.issues || []).length)
      .filter((p) => !ql || p.title.toLowerCase().includes(ql) || p.name.includes(ql) || (p.headings || []).some((h) => h.text.toLowerCase().includes(ql)) || (p.meta?.tags || []).some((t) => t.includes(ql)) || String(p.body || '').toLowerCase().includes(ql))
      .sort((a, b) => (b.meta?.updated || '').localeCompare(a.meta?.updated || '') || a.title.localeCompare(b.title));
  }, [pages, kind, tag, status, conf, qText, only]);

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
      <div className="brain-filters">
        <input className="cs-input" placeholder="Search title, headings, tags, body…" value={qText} onChange={(e) => set('q', e.target.value)} aria-label="Search pages" />
        {['', 'system', 'decision', 'lesson', 'runbook', 'concept', 'skill'].map((k) => (
          <button type="button" key={k || 'all'} className={`cs-chip${kind === k ? ' selected' : ''}`} onClick={() => set('kind', k)}>{k ? KIND_LABEL[k] : 'All kinds'}</button>
        ))}
        <select className="cs-input" style={{ width: 'auto' }} value={status} onChange={(e) => set('status', e.target.value)} aria-label="Status"><option value="">Any status</option><option value="active">active</option><option value="superseded">superseded</option></select>
        <select className="cs-input" style={{ width: 'auto' }} value={conf} onChange={(e) => set('conf', e.target.value)} aria-label="Confidence"><option value="">Any confidence</option><option value="high">high</option><option value="medium">medium</option><option value="low">low</option></select>
        <label className="u-small u-cluster" style={{ '--gap': 'var(--sp-1)' }}><input type="checkbox" checked={only} onChange={(e) => set('issues', e.target.checked ? '1' : '')} /> only pages with issues</label>
        {tag && <button type="button" className="cs-chip selected" onClick={() => set('tag', '')}>tag: {tag} ×</button>}
        <span className="u-small u-muted">{list.length} page{list.length === 1 ? '' : 's'}</span>
      </div>
      <div className="u-scroll-x">
        <table className="cs-table">
          <thead><tr><th>Page</th><th>Kind</th><th>Status</th><th>Confidence</th><th>Updated</th><th className="num">Words</th><th className="num">In / out</th><th className="num">Issues</th></tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="brain-row" onClick={() => navigate(`/brain/${p.id}`)} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/brain/${p.id}`); }}>
                <td><div>{p.title}</div><div className="u-xs u-muted">{(p.meta?.tags || []).join(' · ')}</div></td>
                <td><span className={`cs-badge ${kindBadge(p.kind)}`}>{p.kind}</span></td>
                <td>{p.meta?.status ? <span className={`cs-badge ${statusBadge(p.meta.status)}`}>{p.meta.status}</span> : <span className="u-muted">—</span>}</td>
                <td>{p.meta?.confidence ? <span className={`cs-badge ${confBadge(p.meta.confidence)}`}>{p.meta.confidence}</span> : <span className="u-muted">—</span>}</td>
                <td className="u-tabular">{p.meta?.updated || <span className="u-muted">—</span>}</td>
                <td className="num u-tabular">{p.words}</td>
                <td className="num u-tabular">{(p.linksIn || []).length} / {(p.linksOut || []).length}</td>
                <td className="num">{(p.issues || []).length ? <span className="cs-badge b-warn">{p.issues.length}</span> : <span className="u-muted">0</span>}</td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={8} className="u-muted">No pages match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Gaps ------------------------------------------------------------------
function Gaps({ summary, pages }) {
  const byCode = useMemo(() => {
    const m = {};
    for (const p of pages) for (const i of (p.issues || [])) (m[i.code] = m[i.code] || []).push({ page: p, detail: i.detail });
    return m;
  }, [pages]);
  const cov = summary.coverage || { files: [] };
  const undocumented = (cov.files || []).filter((f) => !f.documented);
  const byDir = undocumented.reduce((acc, f) => { (acc[f.dir] = acc[f.dir] || []).push(f); return acc; }, {});
  const lint = summary.lint || {};
  const codes = Object.keys(ISSUE_LABEL).filter((c) => byCode[c]?.length);
  const total = codes.reduce((a, c) => a + byCode[c].length, 0) + undocumented.length + (lint.errors || []).length;

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-5)' }}>
      <div className={`cs-banner ${total ? 'b-warn' : 'b-ok'}`}>
        {total
          ? <><strong>{total} item{total === 1 ? '' : 's'}</strong> where the brain is thin or inconsistent. Each group says what to do.</>
          : <><strong>Nothing flagged.</strong> Every page links, every field is filled, every source file is named.</>}
      </div>

      {(lint.errors || []).length > 0 && (
        <details className="cs-card brain-gap-group" style={{ padding: 'var(--sp-2) var(--sp-4)' }} open>
          <summary><span className="cs-badge b-danger" style={{ marginRight: 'var(--sp-2)' }}>{lint.errors.length}</span>brain-lint errors (CI fails on these)</summary>
          <ul className="brain-gap-list">{lint.errors.map((e, i) => <li key={i}><code className="cs-code">{e}</code></li>)}</ul>
        </details>
      )}

      {codes.map((code) => (
        <details key={code} className="cs-card brain-gap-group" style={{ padding: 'var(--sp-2) var(--sp-4)' }} open={byCode[code].length <= 8}>
          <summary><span className={`cs-badge ${code === 'broken-link' || code === 'no-frontmatter' ? 'b-danger' : 'b-warn'}`} style={{ marginRight: 'var(--sp-2)' }}>{byCode[code].length}</span>{ISSUE_LABEL[code]} <span className="u-small u-muted" style={{ fontWeight: 400 }}>· {ISSUE_HINT[code]}</span></summary>
          <ul className="brain-gap-list">
            {byCode[code].map((x, i) => <li key={i}><Link to={`/brain/${x.page.id}`}>{x.page.title}</Link> <span className="u-muted">— {x.detail}</span></li>)}
          </ul>
        </details>
      ))}

      <details className="cs-card brain-gap-group" style={{ padding: 'var(--sp-2) var(--sp-4)' }} open={undocumented.length <= 12}>
        <summary><span className={`cs-badge ${undocumented.length ? 'b-warn' : 'b-ok'}`} style={{ marginRight: 'var(--sp-2)' }}>{undocumented.length}</span>Source files no wiki page names <span className="u-small u-muted" style={{ fontWeight: 400 }}>· {cov.documented}/{cov.total} catalogued. A file with no page is knowledge only the code holds.</span></summary>
        {Object.entries(byDir).map(([dir, files]) => (
          <div key={dir} style={{ marginTop: 'var(--sp-3)' }}>
            <div className="u-xs u-muted" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>{dir}</div>
            <ul className="brain-gap-list">
              {files.sort((a, b) => b.bytes - a.bytes).map((f) => <li key={f.path}><code className="cs-code">{f.path.split('/').pop()}</code> <span className="u-muted">· {(f.bytes / 1024).toFixed(1)} KB · last commit {ago(f.gitUpdatedAt)}</span></li>)}
            </ul>
          </div>
        ))}
      </details>

      {(lint.warnings || []).length > 0 && (
        <details className="cs-card brain-gap-group" style={{ padding: 'var(--sp-2) var(--sp-4)' }}>
          <summary><span className="cs-badge b-muted" style={{ marginRight: 'var(--sp-2)' }}>{lint.warnings.length}</span>brain-lint warnings</summary>
          <ul className="brain-gap-list">{lint.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </details>
      )}
    </div>
  );
}

// ---- Log -------------------------------------------------------------------
function LogView({ summary }) {
  const [type, setType] = useState('');
  const [qText, setQ] = useState('');
  const entries = (summary.log || []).filter((e) => !type || e.type === type).filter((e) => !qText || `${e.title} ${e.excerpt}`.toLowerCase().includes(qText.toLowerCase()));
  const types = [...new Set((summary.log || []).map((e) => e.type).filter(Boolean))];
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
      <div className="brain-filters">
        <input className="cs-input" placeholder="Search the log…" value={qText} onChange={(e) => setQ(e.target.value)} aria-label="Search log" />
        <button type="button" className={`cs-chip${!type ? ' selected' : ''}`} onClick={() => setType('')}>all</button>
        {types.map((t) => <button type="button" key={t} className={`cs-chip${type === t ? ' selected' : ''}`} onClick={() => setType(t)}>{t}</button>)}
        <span className="u-small u-muted">{entries.length} of {(summary.log || []).length} shown (newest {summary.log?.length >= 200 ? '200 ' : ''}entries synced)</span>
      </div>
      <div className="cs-card" style={{ padding: '0 var(--sp-4)' }}>
        {entries.map((e, i) => (
          <div key={i} className="brain-log-item">
            <span className="brain-log-date">{fmtDay(e.date)}</span>
            <div>
              <div><strong>{e.title}</strong> {e.type ? <span className="cs-badge b-muted" style={{ marginLeft: 'var(--sp-2)' }}>{e.type}</span> : <span className="cs-badge b-warn" style={{ marginLeft: 'var(--sp-2)' }}>malformed heading</span>}</div>
              <div className="u-small u-muted">{e.excerpt}</div>
              {(e.links || []).length > 0 && <div className="u-cluster u-xs" style={{ '--gap': 'var(--sp-2)', marginTop: 'var(--sp-1)' }}>{e.links.map((l) => <Link key={l} to={`/brain/${l}`}>{l.replace(/^(systems|lessons|decisions|runbooks|concepts)--/, '')}</Link>)}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Platform --------------------------------------------------------------
function Platform() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { brainPlatform().then(setData).catch((e) => setErr(e.message)); }, []);
  if (err) return <div className="cs-banner b-danger">{err}</div>;
  if (!data) return <div className="cs-empty">Loading platform roll-up…</div>;
  const k = data.knowledge || {};
  const bs = (s) => ({ ready: 'b-ok', pending: 'b-info', none: 'b-muted', error: 'b-danger' }[s] || 'b-muted');
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-6)' }}>
      <section>
        <h3 style={{ margin: '0 0 var(--sp-1)' }}>Project brains by organization</h3>
        <p className="u-small u-muted" style={{ marginTop: 0 }}>Each project's embedded index over its documents, deal points, analyses, and correspondence. "none" means never built; "pending" means the worker owes a rebuild.</p>
        {data.orgs.map((o) => (
          <div key={o.id} className="cs-card" style={{ padding: 'var(--sp-4)', marginBottom: 'var(--sp-3)' }}>
            <div className="u-split" style={{ marginBottom: 'var(--sp-3)' }}>
              <div><strong>{o.name}</strong> <span className="u-small u-muted">· {o.members} member{o.members === 1 ? '' : 's'} · {o.projectCount} project{o.projectCount === 1 ? '' : 's'} · {o.totals.docs} docs · {o.totals.chunks.toLocaleString()} chunks indexed</span></div>
              <div className="u-cluster u-xs" style={{ '--gap': 'var(--sp-2)' }}>{Object.entries(o.totals.sources).sort((a, b) => b[1] - a[1]).map(([s, n]) => <span key={s} className="cs-badge b-muted">{s} {n}</span>)}</div>
            </div>
            <div className="u-scroll-x">
              <table className="cs-table">
                <thead><tr><th>Project</th><th>Type</th><th>Brain</th><th className="num">Chunks</th><th className="num">Docs</th><th>Structure note</th><th>Built</th></tr></thead>
                <tbody>
                  {o.projects.map((p) => (
                    <tr key={p.id}>
                      <td><Link to={`/deals/${p.id}?tab=knowledge`}>{p.name}</Link></td>
                      <td className="u-small u-muted">{p.projectType}</td>
                      <td><span className={`cs-badge ${bs(p.brainStatus)}`}>{p.brainStatus}</span></td>
                      <td className="num u-tabular">{p.chunkCount}</td>
                      <td className="num u-tabular">{p.docCount}</td>
                      <td>{p.hasObjective ? <span className="cs-badge b-ok">yes</span> : <span className="cs-badge b-warn">missing</span>}</td>
                      <td className="u-small u-muted">{fmtDate(p.brainBuiltAt)}</td>
                    </tr>
                  ))}
                  {!o.projects.length && <tr><td colSpan={7} className="u-muted">No projects.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </section>
      <section>
        <h3 style={{ margin: '0 0 var(--sp-1)' }}>Knowledge corpus</h3>
        <p className="u-small u-muted" style={{ marginTop: 0 }}>The org-independent <code className="cs-code">knowledge</code> collection: business knowledge and the synced product guide, embedded into <code className="cs-code">chunks</code> for the chat panel, ⌘K, and voice tools. {k.total} docs, {data.chunkDocs ?? '—'} chunk docs.</p>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
          {Object.entries(k.byStatus || {}).map(([s, n]) => <span key={s} className={`cs-badge ${s === 'ready' || s === 'indexed' ? 'b-ok' : s === 'pending' ? 'b-info' : 'b-warn'}`}>{s} {n}</span>)}
          {Object.entries(k.bySource || {}).map(([s, n]) => <span key={s} className="cs-badge b-muted">{s} {n}</span>)}
          {Object.entries(k.byAudience || {}).map(([s, n]) => <span key={s} className="cs-badge b-muted">{s} {n}</span>)}
        </div>
        <div className="u-scroll-x">
          <table className="cs-table">
            <thead><tr><th>Title</th><th>Source</th><th>Audience</th><th>Index</th><th className="num">Chars</th><th>Updated</th></tr></thead>
            <tbody>{(k.docs || []).map((d) => (
              <tr key={d.id}><td>{d.title}<div className="u-xs u-muted">{(d.tags || []).join(' · ')}</div></td><td className="u-small">{d.source}</td><td className="u-small">{d.audience}</td><td><span className={`cs-badge ${d.indexStatus === 'ready' || d.indexStatus === 'indexed' ? 'b-ok' : d.indexStatus === 'pending' ? 'b-info' : 'b-warn'}`}>{d.indexStatus}</span></td><td className="num u-tabular">{d.chars.toLocaleString()}</td><td className="u-small u-muted">{fmtDate(d.updatedAt)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

// ---- Reader ----------------------------------------------------------------
function Reader({ page, pages }) {
  const byName = useMemo(() => new Map(pages.map((p) => [p.name, p.id])), [pages]);
  const resolve = (name) => (byName.has(name) ? `/brain/${byName.get(name)}` : null);
  const body = stripFrontmatter(page.body || '');
  const backlinks = (page.linksIn || []).map((id) => pages.find((p) => p.id === id)).filter(Boolean);
  const outbound = (page.linksOut || []).map((n) => ({ name: n, id: byName.get(n) }));
  const meta = page.meta || {};
  return (
    <div className="brain-reader">
      <article className="cs-card" style={{ padding: 'var(--sp-6)' }}>
        <div className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)', marginBottom: 'var(--sp-2)' }}>
          <span className={`cs-badge ${kindBadge(page.kind)}`}>{page.kind}</span>
          {meta.status && <span className={`cs-badge ${statusBadge(meta.status)}`}>{meta.status}</span>}
          {meta.confidence && <span className={`cs-badge ${confBadge(meta.confidence)}`}>confidence {meta.confidence}</span>}
          {meta.updated && <span className="u-muted">updated {meta.updated}</span>}
          <span className="u-muted">· {page.words} words</span>
        </div>
        <Markdown text={body} resolveWiki={resolve} />
      </article>
      <aside className="brain-rail">
        <div className="cs-card" style={{ padding: 'var(--sp-4)' }}>
          <h4>File</h4>
          <code className="cs-code u-small" style={{ wordBreak: 'break-all' }}>{page.path}</code>
          <div className="u-xs u-muted" style={{ marginTop: 'var(--sp-2)' }}>last commit {ago(page.gitUpdatedAt)} · synced {ago(page.syncedAt)}</div>
          {(meta.tags || []).length > 0 && <div className="u-cluster" style={{ '--gap': 'var(--sp-1)', marginTop: 'var(--sp-2)' }}>{meta.tags.map((t) => <Link key={t} to={`/brain?tab=catalogue&tag=${encodeURIComponent(t)}`} className="cs-chip">{t}</Link>)}</div>}
        </div>
        {(page.issues || []).length > 0 && (
          <div className="cs-card" style={{ padding: 'var(--sp-4)', borderColor: 'var(--warn-border)' }}>
            <h4>Issues</h4>
            <ul>{page.issues.map((i, k) => <li key={k}><span className="cs-badge b-warn" style={{ marginRight: 'var(--sp-1)' }}>{i.code}</span><span className="u-muted">{i.detail}</span></li>)}</ul>
          </div>
        )}
        {(page.headings || []).length > 1 && (
          <div className="cs-card" style={{ padding: 'var(--sp-4)' }}>
            <h4>Contents</h4>
            <ul>{page.headings.filter((h) => h.level > 1).map((h, k) => <li key={k} className={`lvl-${h.level}`}><a href={`#${h.text.toLowerCase().replace(/[^\w]+/g, '-').replace(/^-|-$/g, '')}`}>{h.text}</a></li>)}</ul>
          </div>
        )}
        {(meta.sources || []).length > 0 && (
          <div className="cs-card" style={{ padding: 'var(--sp-4)' }}>
            <h4>Sources</h4>
            <ul>{(Array.isArray(meta.sources) ? meta.sources : [meta.sources]).map((s, k) => <li key={k}><code className="cs-code u-xs" style={{ wordBreak: 'break-all' }}>{s}</code></li>)}</ul>
          </div>
        )}
        <div className="cs-card" style={{ padding: 'var(--sp-4)' }}>
          <h4>Linked from ({backlinks.length})</h4>
          {backlinks.length ? <ul>{backlinks.map((p) => <li key={p.id}><Link to={`/brain/${p.id}`}>{p.title}</Link></li>)}</ul> : <p className="u-small u-muted" style={{ margin: 0 }}>Nothing links here yet.</p>}
        </div>
        {outbound.length > 0 && (
          <div className="cs-card" style={{ padding: 'var(--sp-4)' }}>
            <h4>Links to ({outbound.length})</h4>
            <ul>{outbound.map((o) => <li key={o.name}>{o.id ? <Link to={`/brain/${o.id}`}>{o.name}</Link> : <span style={{ color: 'var(--danger)' }}>{o.name} (missing)</span>}</li>)}</ul>
          </div>
        )}
      </aside>
    </div>
  );
}

// ---- Page ------------------------------------------------------------------
export default function Brain() {
  const superadmin = useSuperadmin();
  const { pageId } = useParams();
  const [params, setParams] = useSearchParams();
  const { docs: pages, error } = useCollection(superadmin ? 'brainPages' : null);
  const { doc: summary } = useDoc(superadmin ? 'brainMeta/summary' : null);
  const tab = params.get('tab') || 'overview';
  const go = (t, extra = {}) => { const n = new URLSearchParams(); n.set('tab', t); for (const [k, v] of Object.entries(extra)) n.set(k, v); setParams(n); };

  if (!superadmin) {
    return (
      <div className="cs-page">
        <div className="cs-page-head"><h1>The Brain</h1></div>
        <p className="cs-help">Operator console. Your account does not carry the superadmin claim.</p>
      </div>
    );
  }
  if (error) return <div className="cs-page"><div className="cs-banner b-danger">{error}</div></div>;
  if (!pages || summary === undefined) return <div className="cs-page"><div className="cs-empty">Loading the brain…</div></div>;
  if (!summary) {
    return (
      <div className="cs-page">
        <div className="cs-page-head"><h1>The Brain</h1></div>
        <div className="cs-banner b-warn">Nothing synced yet. Run <code className="cs-code">node ../scripts/sync-brain.mjs</code> from <code className="cs-code">backend/</code>.</div>
      </div>
    );
  }

  if (pageId) {
    const page = pages.find((p) => p.id === pageId);
    return (
      <div className="cs-page">
        <div className="cs-page-head">
          <div>
            <div className="u-small"><Link to="/brain?tab=catalogue">← The Brain</Link></div>
            <h1 style={{ marginTop: 'var(--sp-1)' }}>{page ? page.title : 'Page not found'}</h1>
          </div>
        </div>
        {page ? <Reader page={page} pages={pages} /> : <p className="cs-help">No page with id <code className="cs-code">{pageId}</code>. It may have been renamed since the last sync.</p>}
      </div>
    );
  }

  const lint = summary.lint || {};
  return (
    <div className="cs-page">
      <div className="cs-page-head">
        <div>
          <h1>The Brain</h1>
          <div className="brain-head-meta u-small u-muted">
            <span>Everything this app knows about itself: {summary.totals?.wiki} wiki pages, {summary.totals?.skills} skills, {summary.totals?.logEntries} log entries.</span>
            <span>Synced {ago(summary.syncedAt)} at <code className="cs-code">{summary.gitHead}</code></span>
            <span className={`cs-badge ${lint.ok ? 'b-ok' : 'b-danger'}`}>{lint.ok ? 'lint clean' : `lint: ${(lint.errors || []).length} errors`}</span>
          </div>
        </div>
      </div>
      <Tabs
        active={tab}
        onChange={(t) => go(t)}
        tabs={[
          { id: 'overview', label: 'Overview', render: () => <Overview summary={summary} pages={pages} go={go} /> },
          { id: 'catalogue', label: `Catalogue (${summary.totals?.wiki || 0})`, render: () => <Catalogue pages={pages} params={params} setParams={setParams} /> },
          { id: 'gaps', label: `Gaps${summary.pagesWithIssues ? ` (${summary.pagesWithIssues})` : ''}`, render: () => <Gaps summary={summary} pages={pages} /> },
          { id: 'log', label: 'Log', render: () => <LogView summary={summary} /> },
          { id: 'platform', label: 'Platform', render: () => <Platform /> },
        ]}
      />
    </div>
  );
}
